/**
 * probeAudioDuration.js — client-side audio duration probe.
 *
 * Reads the file with a hidden <audio> element and resolves with the
 * duration in seconds (or `null` if the browser can't decode). Used by
 * the audiobook / episode upload forms to reject files that fall outside
 * the platform's 5-22 minute window BEFORE we start the upload — matches
 * the server-side guard in `/app/backend/audiobook_series.py` and
 * `/app/backend/cinematic_series.py`.
 */
export const MIN_AUDIO_SECONDS = 5 * 60;
export const MAX_AUDIO_SECONDS = 22 * 60;

export function probeAudioDuration(file) {
  return new Promise((resolve) => {
    if (!file) return resolve(null);
    let url = null;
    try {
      url = URL.createObjectURL(file);
    } catch {
      return resolve(null);
    }
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    };
    audio.onloadedmetadata = () => {
      const d = Number.isFinite(audio.duration) ? audio.duration : null;
      cleanup();
      resolve(d);
    };
    audio.onerror = () => {
      cleanup();
      resolve(null);
    };
    // Some browsers stall — bail after 5s and let the upload proceed
    // (server side will still reject if the file is out of range).
    setTimeout(() => {
      cleanup();
      resolve(null);
    }, 5000);
    audio.src = url;
  });
}

/**
 * Convenience wrapper: probes the file, checks the 5-22 min window,
 * returns { ok, duration, message }. `ok=true` if within range OR if
 * duration couldn't be determined (fall through to server enforcement).
 */
export async function validateAudioDuration(file) {
  const duration = await probeAudioDuration(file);
  if (duration == null) {
    return { ok: true, duration: null, message: null };
  }
  if (duration < MIN_AUDIO_SECONDS) {
    const mm = Math.floor(duration / 60);
    const ss = Math.floor(duration % 60);
    return {
      ok: false,
      duration,
      message: `Audio too short — minimum 5 minutes (yours: ${mm}:${String(ss).padStart(2, '0')}).`,
    };
  }
  if (duration > MAX_AUDIO_SECONDS) {
    const mm = Math.floor(duration / 60);
    const ss = Math.floor(duration % 60);
    return {
      ok: false,
      duration,
      message: `Audio too long — maximum 22 minutes (yours: ${mm}:${String(ss).padStart(2, '0')}).`,
    };
  }
  return { ok: true, duration, message: null };
}
